import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api/client';
import toast from 'react-hot-toast';
import {
  Users, Stethoscope, Activity, Shield, TrendingUp, Clock,
  AlertTriangle, Ban, UserCheck, CheckCircle, ArrowRight,
  RefreshCw, Cpu, AlertCircle, Server
} from 'lucide-react';
import LoadingSkeleton from '../../components/ui/LoadingSkeleton';

/**
 * AdminHome — Action-oriented platform dashboard.
 * Three clear widgets: SLA / Attention, Escalation Queue, System Health.
 */
const AdminHome = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [queueStatus, setQueueStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastFetched, setLastFetched] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [overviewRes, queueRes] = await Promise.allSettled([
        api.get('/admin/analytics/overview'),
        api.get('/risk-assessments/queue/status'),
      ]);
      if (overviewRes.status === 'fulfilled' && overviewRes.value?.data?.success) {
        setStats(overviewRes.value.data.data);
      }
      if (queueRes.status === 'fulfilled' && queueRes.value?.data?.success) {
        setQueueStatus(queueRes.value.data.data);
      }
      setLastFetched(new Date());
    } catch (err) {
      console.error('Admin dashboard fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  if (loading) {
    return (
      <div className="space-y-6 py-6">
        <LoadingSkeleton count={3} variant="stat" cols={3} />
      </div>
    );
  }

  const pendingVerifications = stats?.pendingDoctors || 0;
  const urgentCases = stats?.urgentSymptoms || 0;
  const highRisk = stats?.highRiskPatients || 0;
  const suspendedUsers = stats?.suspendedUsers || 0;
  const activeCases = stats?.activeSymptoms || 0;

  // "Needs Attention" score — total items requiring admin action
  const attentionItems = [
    pendingVerifications > 0 && { label: `${pendingVerifications} doctor verification${pendingVerifications !== 1 ? 's' : ''} pending`, path: '/admin/doctors', severity: 'high' },
    urgentCases > 0 && { label: `${urgentCases} urgent open case${urgentCases !== 1 ? 's' : ''}`, path: '/admin/population', severity: 'critical' },
    suspendedUsers > 0 && { label: `${suspendedUsers} suspended account${suspendedUsers !== 1 ? 's' : ''}`, path: '/admin/users', severity: 'low' },
  ].filter(Boolean);

  const queueHealthy = !queueStatus?.failedJobs || queueStatus.failedJobs === 0;
  const failedJobs = queueStatus?.failedJobs || 0;
  const pendingJobs = queueStatus?.pendingJobs || queueStatus?.waiting || 0;
  const processingJobs = queueStatus?.activeJobs || queueStatus?.active || 0;

  const severityDot = { critical: 'bg-red-500', high: 'bg-amber-500', low: 'bg-slate-400' };

  return (
    <div className="space-y-8 py-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="font-headline text-2xl font-extrabold text-slate-900 tracking-tight">Platform Command</h2>
          <p className="text-slate-500 text-sm mt-1">
            {lastFetched ? `Updated ${Math.round((Date.now() - lastFetched) / 1000)}s ago` : 'Loading…'}
          </p>
        </div>
        <button
          onClick={fetchData}
          disabled={loading}
          className="p-2.5 rounded-xl border border-slate-200 text-slate-500 hover:bg-slate-50 transition-all disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Top metric strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: 'Total Patients', value: stats?.totalPatients || 0, icon: Users, color: 'text-blue-600 bg-blue-50' },
          { label: 'Approved Doctors', value: stats?.approvedDoctors || 0, icon: Stethoscope, color: 'text-teal-600 bg-teal-50' },
          { label: 'Active Cases', value: activeCases, icon: Activity, color: 'text-amber-600 bg-amber-50' },
          { label: 'High-Risk Patients', value: highRisk, icon: AlertTriangle, color: 'text-rose-600 bg-rose-50' },
        ].map((m) => {
          const Icon = m.icon;
          return (
            <div key={m.label} className="bg-white rounded-2xl border border-outline-variant/10 p-4 shadow-sm">
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center mb-3 ${m.color}`}>
                <Icon className="w-5 h-5" />
              </div>
              <p className="text-2xl font-extrabold text-slate-900">{m.value}</p>
              <p className="text-xs font-semibold text-slate-500 mt-0.5">{m.label}</p>
            </div>
          );
        })}
      </div>

      {/* 3 Widget Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* Widget 1: Needs Attention */}
        <div className="bg-white rounded-2xl border border-outline-variant/10 shadow-sm p-6 flex flex-col">
          <div className="flex items-center gap-3 mb-4">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${attentionItems.length > 0 ? 'bg-red-50' : 'bg-emerald-50'}`}>
              {attentionItems.length > 0
                ? <AlertCircle className="w-5 h-5 text-red-600" />
                : <CheckCircle className="w-5 h-5 text-emerald-600" />
              }
            </div>
            <div>
              <p className="text-xs font-extrabold text-slate-500 uppercase tracking-widest">Needs Attention</p>
              <h3 className="font-bold text-slate-900">
                {attentionItems.length > 0 ? `${attentionItems.length} item${attentionItems.length !== 1 ? 's' : ''}` : 'All clear'}
              </h3>
            </div>
          </div>

          {attentionItems.length === 0 ? (
            <p className="text-sm text-slate-500 flex-1">No outstanding administrative actions.</p>
          ) : (
            <ul className="space-y-2 flex-1">
              {attentionItems.map((item, i) => (
                <li key={i}>
                  <button
                    onClick={() => navigate(item.path)}
                    className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-slate-50 transition-all text-left"
                  >
                    <span className={`w-2 h-2 rounded-full shrink-0 ${severityDot[item.severity]}`} />
                    <span className="text-sm font-semibold text-slate-700 flex-1">{item.label}</span>
                    <ArrowRight className="w-4 h-4 text-slate-400 shrink-0" />
                  </button>
                </li>
              ))}
            </ul>
          )}

          {urgentCases > 0 && (
            <button
              onClick={() => navigate('/admin/population')}
              className="mt-4 flex items-center justify-center gap-2 w-full py-2.5 bg-red-600 text-white rounded-xl text-sm font-bold hover:bg-red-700 transition-all"
            >
              Review Urgent Cases <ArrowRight className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Widget 2: Escalation & High Risk */}
        <div className="bg-white rounded-2xl border border-outline-variant/10 shadow-sm p-6 flex flex-col">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-amber-50">
              <Shield className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-xs font-extrabold text-slate-500 uppercase tracking-widest">Risk Overview</p>
              <h3 className="font-bold text-slate-900">Patient Risk Levels</h3>
            </div>
          </div>

          <div className="space-y-3 flex-1">
            {[
              { label: 'High Risk', value: highRisk, color: 'bg-red-500', bar: highRisk, max: Math.max(highRisk, stats?.totalPatients || 1) },
              { label: 'Active Cases', value: activeCases, color: 'bg-amber-500', bar: activeCases, max: Math.max(activeCases, stats?.totalPatients || 1) },
              { label: 'Urgent', value: urgentCases, color: 'bg-red-700', bar: urgentCases, max: Math.max(urgentCases, stats?.totalPatients || 1) },
            ].map((item) => (
              <div key={item.label}>
                <div className="flex justify-between text-xs font-bold text-slate-600 mb-1">
                  <span>{item.label}</span>
                  <span>{item.value}</span>
                </div>
                <div className="w-full bg-slate-100 rounded-full h-2">
                  <div
                    className={`${item.color} h-2 rounded-full transition-all`}
                    style={{ width: `${Math.min((item.bar / item.max) * 100, 100)}%` }}
                  />
                </div>
              </div>
            ))}
          </div>

          <button
            onClick={() => navigate('/admin/population')}
            className="mt-5 flex items-center justify-center gap-2 w-full py-2.5 border border-slate-200 text-slate-700 rounded-xl text-sm font-bold hover:bg-slate-50 transition-all"
          >
            Population Health <ArrowRight className="w-4 h-4" />
          </button>
        </div>

        {/* Widget 3: System Health */}
        <div className="bg-white rounded-2xl border border-outline-variant/10 shadow-sm p-6 flex flex-col">
          <div className="flex items-center gap-3 mb-4">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${queueHealthy ? 'bg-emerald-50' : 'bg-red-50'}`}>
              <Server className={`w-5 h-5 ${queueHealthy ? 'text-emerald-600' : 'text-red-600'}`} />
            </div>
            <div>
              <p className="text-xs font-extrabold text-slate-500 uppercase tracking-widest">System Health</p>
              <h3 className={`font-bold ${queueHealthy ? 'text-emerald-700' : 'text-red-700'}`}>
                {queueHealthy ? 'All systems normal' : `${failedJobs} failed job${failedJobs !== 1 ? 's' : ''}`}
              </h3>
            </div>
          </div>

          <div className="space-y-3 flex-1">
            {[
              { label: 'Processing Queue', value: processingJobs, icon: Cpu, ok: processingJobs < 50 },
              { label: 'Pending Jobs', value: pendingJobs, icon: Clock, ok: pendingJobs < 100 },
              { label: 'Failed Jobs', value: failedJobs, icon: AlertTriangle, ok: failedJobs === 0 },
              { label: 'Suspended Accounts', value: suspendedUsers, icon: Ban, ok: true },
            ].map((item) => {
              const Icon = item.icon;
              return (
                <div key={item.label} className="flex items-center gap-3 p-2.5 rounded-xl bg-slate-50">
                  <Icon className={`w-4 h-4 shrink-0 ${item.ok ? 'text-slate-400' : 'text-red-500'}`} />
                  <span className="text-sm text-slate-600 flex-1">{item.label}</span>
                  <span className={`text-sm font-bold ${item.ok ? 'text-slate-700' : 'text-red-600'}`}>{item.value}</span>
                </div>
              );
            })}
          </div>

          {!queueHealthy && (
            <button
              onClick={async () => {
                try {
                  await api.post('/risk-assessments/queue/retry', { limit: 10 });
                  await fetchData();
                } catch (err) {
                  console.error('Queue retry failed:', err);
                  toast.error('Failed to retry jobs — check server logs');
                }
              }}
              className="mt-4 flex items-center justify-center gap-2 w-full py-2.5 bg-red-600 text-white rounded-xl text-sm font-bold hover:bg-red-700 transition-all"
            >
              Retry Failed Jobs <RefreshCw className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Recent Activity */}
      {stats?.recentEvents?.length > 0 && (
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-outline-variant/10">
          <h3 className="font-bold text-base text-slate-900 mb-4">Recent Platform Activity</h3>
          <div className="divide-y divide-slate-50">
            {stats.recentEvents.map((evt) => (
              <div key={evt.id} className="flex items-center gap-3 py-3">
                <div className={`w-2 h-2 rounded-full shrink-0 ${evt.severity === 'high' || evt.severity === 'urgent' ? 'bg-red-500' : evt.severity === 'medium' ? 'bg-amber-500' : 'bg-slate-300'}`} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-700 truncate capitalize">{evt.type?.replace(/_/g, ' ')}</p>
                  <p className="text-xs text-slate-500">{evt.patient}</p>
                </div>
                <span className="text-xs text-slate-400 shrink-0">{evt.timestamp ? new Date(evt.timestamp).toLocaleString() : ''}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quick nav */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {[
          { label: 'Doctor Management', desc: 'Approve and manage doctors', path: '/admin/doctors', icon: Stethoscope, badge: pendingVerifications > 0 ? pendingVerifications : null },
          { label: 'On-Call Schedule', desc: 'Manage doctor rotations', path: '/admin/on-call', icon: Clock },
          { label: 'Population Health', desc: 'Aggregate analytics', path: '/admin/population', icon: TrendingUp },
        ].map((link) => {
          const Icon = link.icon;
          return (
            <button
              key={link.path}
              onClick={() => navigate(link.path)}
              className="bg-white rounded-2xl p-5 border border-outline-variant/10 hover:shadow-md transition-all text-left relative"
            >
              {link.badge != null && (
                <span className="absolute top-4 right-4 px-2 py-0.5 rounded-full bg-red-600 text-white text-xs font-bold">{link.badge}</span>
              )}
              <Icon className="w-7 h-7 text-primary mb-3" />
              <h4 className="font-bold text-sm text-slate-900">{link.label}</h4>
              <p className="text-xs text-slate-500 mt-1">{link.desc}</p>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default AdminHome;
